/**
 * Displays the player's current score.
 *
 * When the score changes, the text does a quick zoom-in pulse to
 * draw the player's attention to the update.
 */

const TEXT_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '14px',
  color: '#ffffff',
};

export default class ScoreDisplay {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x
   * @param {number} y
   */
  constructor(scene, x, y) {
    this.scene = scene;
    this.text = scene.add.text(x, y, 'SCORE: 0', TEXT_STYLE);
    this.text.setOrigin(0, 0.5);

    /** @private Track last score to detect changes. */
    this._lastScore = 0;
  }

  /**
   * Update the displayed score.
   * If the score increased, play a quick zoom pulse on the text.
   * If the score decreased, play a subtle red flash to indicate cost.
   *
   * @param {number} score
   */
  setScore(score) {
    this.text.setText(`SCORE: ${score}`);

    if (score > this._lastScore) {
      this._playPulse();
    } else if (score < this._lastScore) {
      this._playDecreaseFeedback();
    }
    this._lastScore = score;
  }

  /** Clean up. */
  destroy() {
    if (this.text) {
      this.text.destroy();
      this.text = null;
    }
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Quick zoom-in + zoom-out pulse on the score text.
   * @private
   */
  _playPulse() {
    if (!this.text || !this.scene) return;

    // Kill any in-progress pulse so they don't stack
    this.scene.tweens.killTweensOf(this.text);

    this.text.setScale(1);
    this.scene.tweens.add({
      targets: this.text,
      scaleX: 1.25,
      scaleY: 1.25,
      duration: 100,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => {
        if (this.text) this.text.setScale(1);
      },
    });
  }

  /**
   * Subtle visual feedback when score decreases (e.g. rest cost).
   * Briefly tints the text dim gray, then restores to white.
   * Less dramatic than the increase pulse — just enough to notice.
   * @private
   */
  _playDecreaseFeedback() {
    if (!this.text || !this.scene) return;

    // Don't interrupt an existing decrease feedback
    if (this._decreasingFeedback) return;
    this._decreasingFeedback = true;

    // Flash to dim gray
    this.text.setStyle({ color: '#666666' });

    // Restore after a short delay
    this.scene.time.delayedCall(150, () => {
      if (this.text && this.text.active) {
        this.text.setStyle({ color: '#ffffff' });
      }
      this._decreasingFeedback = false;
    });
  }
}
